import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CompanyData } from "@/pages/companies/types";
import { useCompanyActions } from "@/services/hooks/entities/company/useCompanyActions";
import SingleInput from "@/components/controls/SingleInput";
import { cn } from "@/utils/utils";
import Text from "@/components/ui/text";
import {
  FieldConfig,
  FieldItem,
  FieldsConfig
} from "@/components/layouts/DetailFields";
import {
  createGetFieldKey,
  getFieldValue,
  handleFieldError
} from "@/pages/deals/components/commercials/ContractTerms/ContractTerms";
import IconContainer from "@/components/ui/icon-container";
import { InfoCircle } from "@dreamhub/designsystem/icons";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { getCurrencySymbol } from "@/pages/deals/components/commercials/DealCommercialsV2/ContractTermsV2/consts";

const TRANSLATION_PREFIX = "companies.commercials";

interface Props {
  companyData: CompanyData;
  contractCurrency: string | null;
}

const CompanyFinancialsSection: React.FC<Props> = ({
  companyData,
  contractCurrency
}) => {
  const { t } = useTranslation();
  const { company, onUpdateField } = useCompanyActions(companyData);

  // Get currency symbol from contract currency or default to USD
  const currencySymbol = getCurrencySymbol(contractCurrency ?? "USD");

  const getFieldKey = useCallback(createGetFieldKey(company as any), [
    company?.id,
    company?.arr,
    company?.mrr
  ]);

  const [fieldErrors, setFieldErrors] = useState<{
    [fieldName: string]: string;
  }>({});

  useEffect(() => {
    setFieldErrors({});
  }, [company?.id]);

  const fieldsConfig: FieldsConfig = {
    mrr: {
      label: t(`${TRANSLATION_PREFIX}.totalMrr`),
      type: "editableInput",
      isMonetary: true,
      maxDigits: 10,
      currencySymbol
    },
    arr: {
      label: t(`${TRANSLATION_PREFIX}.totalArr`),
      type: "editableInput",
      isMonetary: true,
      maxDigits: 10,
      currencySymbol
    },
    annualContractValue: {
      label: t(`${TRANSLATION_PREFIX}.annualContractValueAcv`),
      infoTooltip: t(`${TRANSLATION_PREFIX}.acvTooltip`),
      type: "editableInput",
      isMonetary: true,
      maxDigits: 10,
      currencySymbol
    },
    totalContractValue: {
      label: t(`${TRANSLATION_PREFIX}.totalContractValueTcv`),
      infoTooltip: t(`${TRANSLATION_PREFIX}.tcvTooltip`),
      type: "editableInput",
      isMonetary: true,
      maxDigits: 10,
      currencySymbol
    }
  };

  const renderFieldComponent = (fieldName: string, config: FieldConfig) => {
    const value = config?.selectedValue
      ? config?.selectedValue
      : getFieldValue(company as any, fieldName);

    let switchComponent;
    switch (config.type) {
      case "editableInput":
        switchComponent = (
          <SingleInput
            key={getFieldKey(fieldName as any)}
            fieldName={fieldName}
            initialValue={
              value?.toString() ?? config.initialValue?.toString() ?? ""
            }
            onSave={(newValue: string) =>
              onUpdateField(
                fieldName,
                newValue !== "" && !isNaN(Number(newValue))
                  ? Number(newValue)
                  : newValue
              )
            }
            textVariant="body"
            validation={config.validation}
            isMonetary={config.isMonetary}
            isPretty={config.isPretty}
            onError={(error) =>
              handleFieldError(
                setFieldErrors,
                fieldName,
                !!error,
                error || undefined
              )
            }
            currencySymbol={config?.currencySymbol}
            maxDigits={config?.maxDigits}
            placeholder={`${t("add")} ${config.label}`}
            {...config.additionalProps}
          />
        );
        break;

      default:
        switchComponent = null;
    }

    return <div className="flex items-center">{switchComponent}</div>;
  };

  // Left column: MRR/ARR fields, Right column: ACV/TCV fields
  const leftFieldNames = ["mrr", "arr"];
  const rightFieldNames = ["annualContractValue", "totalContractValue"];

  const getOrderedFieldItemsForGroup = useCallback(
    (fieldGroup: string[]) => {
      const fieldItems: Record<string, FieldItem> = {};
      for (const fieldName of fieldGroup) {
        if (fieldsConfig[fieldName]) {
          fieldItems[fieldName] = {
            component: renderFieldComponent(fieldName, fieldsConfig[fieldName]),
            icon: fieldsConfig[fieldName].icon || ""
          };
        }
      }
      return fieldItems;
    },
    [renderFieldComponent]
  );

  const renderField = (fieldName: string, fieldItem: FieldItem) => (
    <div className="group contents">
      <div
        className={cn(
          "flex gap-2 overflow-hidden pr-4 h-9",
          fieldErrors[fieldName] ? "items-start pt-2" : "items-center"
        )}
      >
        <Text variant="bodySecondary" className="truncate" hasTruncatedTooltip>
          {fieldsConfig[fieldName].label ||
            t(`companies.companyView.${fieldName}`)}
        </Text>
        {fieldsConfig[fieldName]?.infoTooltip && (
          <Tooltip>
            <TooltipTrigger>
              <IconContainer color="secondary">
                <InfoCircle />
              </IconContainer>
            </TooltipTrigger>
            <TooltipContent maxWidth={18}>
              {fieldsConfig[fieldName]?.infoTooltip}
            </TooltipContent>
          </Tooltip>
        )}
      </div>
      <div className="flex items-center overflow-hidden break-words h-9">
        {fieldItem.component}
      </div>
    </div>
  );

  const renderFieldsContainer = (
    fieldOrder: string[],
    fields: Record<string, FieldItem>
  ) => (
    <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 items-center">
      {fieldOrder.map(
        (fieldName) =>
          fields[fieldName] && (
            <React.Fragment key={fieldName}>
              {renderField(fieldName, fields[fieldName])}
            </React.Fragment>
          )
      )}
    </div>
  );

  const leftFields = useMemo(
    () => getOrderedFieldItemsForGroup(leftFieldNames),
    [getOrderedFieldItemsForGroup]
  );

  const rightFields = useMemo(
    () => getOrderedFieldItemsForGroup(rightFieldNames),
    [getOrderedFieldItemsForGroup]
  );

  return (
    <div className="flex flex-col gap-4">
      <Text variant="smSubtitle">
        {t(`${TRANSLATION_PREFIX}.companyFinancials`)}
      </Text>
      <div className="flex flex-col gap-4 p-6 bg-surface-elevation1 rounded-xl border border-border-border1">
        <div className="grid grid-cols-2 gap-8">
          {renderFieldsContainer(leftFieldNames, leftFields)}
          {renderFieldsContainer(rightFieldNames, rightFields)}
        </div>
      </div>
    </div>
  );
};

export default CompanyFinancialsSection;
